import PropTypes from 'prop-types';
import React, {Component} from 'react';
import moment from 'moment';

//Utils
import events from '../../../../lib/events.js';
import {formatCurrency} from '../../../helpers/dashboardFormatter.jsx';
import {dateTimeFormats} from 'helpers/constants';
import {formatStaticCurrency} from '../../../helpers/currencyHelper.jsx';

//Components
import ReportDropDown from '../components/reportDropDown.jsx';
import PrintableSummary from '../components/printableSummary.jsx';
import Loader from '../../../components/loader.jsx';

//Apis
import ReportApi from '../../../../api/report.jsx';

import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import ReportSummaryErrorHandler from '../components/reportSummaryErrorHandler';
import {makeCancelable} from 'helpers/cancelablePromise';

const filterOptions = {
  branch: -1,
  branchOptions: [],
  poolType: -1,
  poolTypeOptions: [],
  customerType: '',
  customerTypeOptions: [],
};

class AccountListReport extends Component {
  constructor(props) {
    super(props);

    let {formatMessage} = this.props.intl;

    const _filterOptions =  Object.assign({},filterOptions);
    const branchesFilterable = this.props.branches.map(b => {
      return {
        value: b.id,
        label: b.name,
      };
    });

    _filterOptions.branchOptions = [
      {
        value: -1,
        label: formatMessage({id: 'general.all.stores'}),
      }, ...branchesFilterable];

    _filterOptions.poolTypeOptions =  [
      {
        value: -1,
        label: formatMessage({id: 'general.all.systems'}),
      },
      {
        value: 1,
        label: formatMessage({id: 'general.pools'}),
      },
      {
        value: 2,
        label: formatMessage({id: 'general.spas'}),
      },
      {
        value: 3,
        label: formatMessage({id: 'general.swim.spas'}),
      },
    ];

    _filterOptions.customerTypeOptions = [
      {
        value: '',
        label: formatMessage({id: 'general.all'}),
      },
      {
        value: true,
        label: formatMessage({id: 'general.active'}),
      },
      {
        value: false,
        label: formatMessage({id: 'general.inactive'}),
      },
    ];

    this.state = {
      loaded: true,
      summary: null,
      report: null,
      branches: this.props.branches.map(b => b.id),
      showNoDataMessage: false,
      error: false,
      ..._filterOptions,
    };
  }

  componentWillUnmount() {
    this.setState({
      filterOptions: {},
    });
  }

  getElementValue = (value, format) => {
    if(!value || value === 0) {
      return '-';
    } else if(format) {
      return format(value);
    } else {
      return value;
    }
  };

  exportReport = () => {
    const {branch, poolType, customerType, branches} = this.state;

    ReportApi.getAccountListExport(
      this.props.intl.locale,
      branch === -1 ? branches : branch,
      poolType === -1 ? null : poolType,
      customerType === '' ? null : customerType);
  };

  printReport = () => {
    const {summary} = this.state;
    let {formatMessage} = this.props.intl;
    const columns = [
      {name: formatMessage({id: 'general.total.accounts'}), key: 'total'},
      {name: formatMessage({id: 'general.active.accounts'}), key: 'active'} ,
      {name: formatMessage({id: 'general.inactive.accounts'}), key: 'inactive'},
      {name: formatMessage({id: 'general.total.pools'}), key: 'pools'},
      {name: formatMessage({id: 'general.in.ground.pools'}), key: 'inground'},
      {name: formatMessage({id: 'general.above.ground.pools'}), key: 'aboveground'},
      {name: formatMessage({id: 'general.splasher.pools'}), key: 'splasher'},
      {name: formatMessage({id: 'general.swim.spas'}), key: 'swimspas'},
      {name: formatMessage({id: 'general.spas'}), key: 'spas'},
      {name: formatMessage({id: 'general.account.without.systems'}), key: 'accountWithoutSystems'},
      {name: formatMessage({id: 'general.account.without.analysis'}), key: 'accountWithoutAnalysis'},
      {name: formatMessage({id: 'general.total.maintenance'}), key: 'totalMaintenanceSystems'},
    ];

    const trublueColumns = [{name: formatMessage({id: 'reports.active.trublue.accounts'}), key: 'truBlueActive'}];

    let posColumns = [
      {name: formatMessage({id: 'reports.total.purchasing.accounts'}), key: 'posAccounts'},
      {name: formatMessage({id: 'reports.purchasing.accounts.value'}), key: 'posSalesSum'},
      {name: formatMessage({id: 'reports.average.purchasing.sales'}), key: 'posAvgSales'},
    ];
    const tempSummary = Object.assign({},summary);

    Object.keys(tempSummary).forEach((key) => {
      tempSummary[key] = this.getElementValue(tempSummary[key]);
    });

    if (this.props.TruBlueActive) {
      columns.splice(3, 0, ...trublueColumns);
    }

    if (this.props.POSActive) {
      columns.push(...posColumns);
      if(tempSummary.posSalesSum && tempSummary.posSalesSum !== '-') {
        tempSummary.posSalesSum = formatStaticCurrency(tempSummary.posSalesSum);
      }
      if(tempSummary.posAvgSales && tempSummary.posAvgSales !== '-') {
        tempSummary.posAvgSales = formatStaticCurrency(tempSummary.posAvgSales);
      }
    }
    return (
      <PrintableSummary summary={tempSummary} columns={columns} title='Account List Summary'
        date={moment().format(dateTimeFormats.numeralDate)} />
    );
  };

  resetFilters = () => {
    this.setState({
      branch: -1,
      customerType: '',
      poolType: -1,
    });
  };

  stateSetter(propName) {
    return value => this.setState({[propName]: value});
  }

  updateReport = () => {
    const {branch, poolType, customerType, branches} = this.state;

    this.setState({
      loaded: false,
      error: false,
    });

    if (this.fetching && !this.fetching.isFinished) {
      this.fetching.cancel();
    }else{
      this.setState({
        summary: null,
      });
    }

    (this.fetching = makeCancelable(ReportApi.getAccountListSummary(
      branch === -1 ? branches : branch,
      poolType === -1 ? null : poolType,
      customerType === '' ? null : customerType)))
      .then(summary => {
        if(summary && summary.total > 0) {
          this.setState({loaded: true, summary: summary, showNoDataMessage: false});
        } else {
          this.setState({loaded: true, showNoDataMessage: true});
        }
      }).catch(err => {
        console.log(err);
        if (err && err.canceled) {return;}
        this.setState({loaded: true, error: true});
      });
  };

  render() {
    const {summary, loaded, customerType, customerTypeOptions, branch, branchOptions, poolType, poolTypeOptions, showNoDataMessage} = this.state;
    const {TruBlueActive} = this.props;
    let {formatMessage} = this.props.intl;

    return (
      <div className='report-container'>
        <span className='description'><FormattedMessage id='reports.all.accounts.description' /></span>
        <div className='report-options'>
          <div className='top-title-bar'>
            <FormattedMessage id='general.report.options' />
          </div>
          <div className='select-options container'>
            <div className='row'>
              <div className='col-sm-3'>
                <ReportDropDown value={branch} label={formatMessage({id: 'general.store'})}
                  onChange={((e) => {
                    this.setState({branch: parseInt(e.target.value)});
                  })}
                  options={branchOptions} />
              </div>
              <div className='col-sm-3'>
                <ReportDropDown value={poolType} label={formatMessage({id: 'general.system.type'})}
                  onChange={((e) => {
                    this.setState({poolType: parseInt(e.target.value)});
                  })}
                  options={poolTypeOptions} />
              </div>
              <div className='col-sm-3'>
                <ReportDropDown value={customerType} label={formatMessage({id: 'general.account.status'})}
                  onChange={((e) => {
                    this.setState({customerType: e.target.value === '' ? '' : e.target.value === 'true'});
                  })}
                  options={customerTypeOptions} />
              </div>
            </div>
          </div>
          <div className='manage-options'>
            <div className='col-sm-12 button-group'>
              <span className='reset-button pull-right' onClick={this.resetFilters}><FormattedMessage id='general.reset' /></span>
              <button className='btn btn-primary pull-right' onClick={this.updateReport}><FormattedMessage id='reports.update.summary' /></button>
            </div>
          </div>
        </div>
        <Loader loaded={loaded}>
          <ReportSummaryErrorHandler error={this.state.error}>
            {summary ? <div className='col-sm-12 summary-container'>
              <div className='row'>
                <h2><FormattedMessage id='reports.report.summary' /></h2>
                <p className='description'><FormattedMessage id='reports.reveal.report.summary' /></p>
              </div>
              <div className='row'>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.total.accounts' /></label>
                  <div>{summary.total}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.active.accounts' /></label>
                  <div>{summary.active}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.inactive.accounts' /></label>
                  <div>{summary.inactive}</div>
                </div>
                {TruBlueActive ?
                  <div className='col-sm-3 result'>
                    <label><FormattedMessage id='reports.active.trublue.accounts' /></label>
                    <div>{summary.truBlueActive}</div>
                  </div> :
                null}
              </div>
              <div className='row'>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.total.pools' /></label>
                  <div>{summary.pools}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.in.ground.pools' /></label>
                  <div>{summary.inground}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.above.ground.pools' /></label>
                  <div>{summary.aboveground}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.splasher.pools' /></label>
                  <div>{summary.splasher}</div>
                </div>
              </div>
              <div className='row'>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.swim.spas' /></label>
                  <div>{summary.swimspas}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.spas' /></label>
                  <div>{summary.spas}</div>
                </div>
              </div>
              <div className='row'>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.total.maintenance' /></label>
                  <div>{summary.totalMaintenanceSystems}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.account.without.systems' /></label>
                  <div>{this.getElementValue(summary.accountWithoutSystems)}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='general.account.without.analysis' /></label>
                  <div>{this.getElementValue(summary.accountWithoutAnalysis)}</div>
                </div>
              </div>
              {this.props.POSActive &&
              <div className='col-sm-12'>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='reports.total.purchasing.accounts' /></label>
                  <div>{this.getElementValue(summary.posAccounts)}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='reports.purchasing.accounts.value' /></label>
                  <div>{formatCurrency(summary.posSalesSum)}</div>
                </div>
                <div className='col-sm-3 result'>
                  <label><FormattedMessage id='reports.average.purchasing.sales' /></label>
                  <div>{formatCurrency(summary.posAvgSales)}</div>
                </div>
              </div>
              }
            </div> :
            <div className='summary-container'>
              <h2><FormattedMessage id='reports.report.summary' /></h2>
              <p><FormattedMessage id='reports.reveal.report.summary' /></p>
              {showNoDataMessage ? <label className='report-no-data'><FormattedMessage id='reports.error.no.results' /></label> : ''}
              <div className='container-fluid custom-container-margins'><div /></div>
            </div>}
            <div className='col-sm-12 button-group shift-right'>
              <button className='btn btn-default pull-right' disabled={(summary === null)} onClick={(() => {
                events.publish('print', this.printReport());
              })}><FormattedMessage id='reports.print.summary' /></button>
              <button className='btn btn-primary pull-right' onClick={this.exportReport}><FormattedMessage id='reports.export.report' /></button>
            </div>
          </ReportSummaryErrorHandler>
        </Loader>
      </div>
    );
  }
}

AccountListReport.propTypes = {
  branches: PropTypes.any,
  POSActive: PropTypes.bool,
  TruBlueActive: PropTypes.bool,
};

AccountListReport.defaultProps = {
  branches: [],
  POSActive: false,
};

export default injectIntl(AccountListReport);
